/**
 * BARRIDO DEL REVEAL · el segundo seguro, para el humano que SI hace scroll.
 *
 * `revealBelt` cubre el caso "no hay nadie delante" (captura `fullPage`,
 * impresion, rastreador). Este modulo cubre el contrario, que es el que quedaba
 * abierto:
 *
 *   INCIDENTE (20260725, medido por FORGE-ECHO): con un barrido rapido — saltos
 *   de 0,8 x viewport cada 60 ms — quedaban 8 bloques en `opacity: 0`. Con
 *   saltos de 250 px cada 90 ms, cero. El `IntersectionObserver` NO estaba
 *   roto: un salto grande puede pasar por encima de un bloque entero entre dos
 *   frames, y el observador solo calcula intersecciones en los frames que hay.
 *   Lo que el observador no ve cruzar, no dispara. Y el cinturon no salva a
 *   nadie ahi, porque la primera senal humana lo cancela para siempre.
 *
 * REGLA: cada vez que el scroll se mueve — durante el gesto y otra vez al
 * pararse — se vuelve a medir con `getBoundingClientRect()` lo que siga
 * pendiente. Es exactamente la medida que `Reveal` ya hace en su primer
 * `requestAnimationFrame`, solo que repetida mientras quede alguien esperando.
 *
 * COSTE: UN solo listener para toda la pagina, no uno por bloque; una pasada
 * por frame como mucho; y en cuanto no queda nadie pendiente se desengancha
 * solo. Una pagina ya revelada no paga nada.
 */

/** Vuelve a medirse y decide si le toca aparecer. La aporta cada `Reveal`. */
type SweepCheck = () => void;

/** Margen tras el ultimo evento de scroll para dar el gesto por terminado. */
const SETTLE_MS = 120;

const pending = new Set<SweepCheck>();
let frame: number | undefined;
let settle: ReturnType<typeof setTimeout> | undefined;
let listening = false;

/** Eventos que pueden dejar un bloque nuevo a la vista sin que nadie lo mire. */
const SCROLL_SIGNALS = ['scroll', 'resize', 'orientationchange'] as const;

function sweep() {
  frame = undefined;
  // Copia: un `check` que revela se da de baja de `pending` durante el bucle.
  for (const check of [...pending]) check();
}

function requestSweep() {
  if (frame === undefined && typeof requestAnimationFrame === 'function') {
    frame = requestAnimationFrame(sweep);
  }
}

function onScrollSignal() {
  // Durante el gesto: una pasada por frame, nunca una por evento.
  requestSweep();
  // Y una ultima al pararse, por si el ultimo frame se comio el salto final.
  if (settle !== undefined) clearTimeout(settle);
  settle = setTimeout(() => {
    settle = undefined;
    requestSweep();
  }, SETTLE_MS);
}

function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  for (const signal of SCROLL_SIGNALS) {
    // `capture: true`: `scroll` no burbujea, y asi tambien llegan los de un
    // contenedor con scroll propio, no solo los del documento.
    window.addEventListener(signal, onScrollSignal, { passive: true, capture: true });
  }
}

function unlisten() {
  if (!listening) return;
  listening = false;
  for (const signal of SCROLL_SIGNALS) {
    window.removeEventListener(signal, onScrollSignal, { capture: true });
  }
  if (frame !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
  frame = undefined;
  if (settle !== undefined) clearTimeout(settle);
  settle = undefined;
}

/**
 * Apunta un bloque al barrido. Devuelve la baja, que hay que llamar en cuanto
 * el bloque se revela (o se desmonta): cuando la lista se vacia, el modulo se
 * desengancha del `window` y deja de costar nada.
 */
export function onRevealSweep(check: SweepCheck): () => void {
  pending.add(check);
  listen();
  return () => {
    pending.delete(check);
    if (pending.size === 0) unlisten();
  };
}

/** Solo para tests: devuelve el modulo a su estado de arranque. */
export function resetRevealSweep() {
  pending.clear();
  unlisten();
}
